import React, { useState, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { FaSearch, FaPlus } from 'react-icons/fa';
import './TableComponent.css';
import useDebounce from '../Comman/debounce/index';

const TableComponent = ({
    columns,
    data = [],
    createButtonText = 'Create New',
    onCreateClick,
    searchPlaceholder = 'Search...',
    onSearch,
    pagination = false,
    itemsPerPage = 10,
    actionButtons,
    cardTitle = 'Data Table',
    hideSearch = false,
    currentPage = 1,
    setCurrentPage
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState(data || []);

    useLayoutEffect(() => {
        if (data) {
            setFilteredData(data);
        }
    }, [data]);

    const searchDebounce=useDebounce(searchTerm,1000);

    // Initialize and filter data
    useEffect(() => {
        // Initialize with empty array if data is undefined
        const initialData = data || [];
        if (onSearch) {
            onSearch(searchDebounce);
        } else {
            const filtered = initialData.filter(item =>
                columns.some(column => {

                    const value = item[column.key];
                    return value && value.toString().toLowerCase().includes(searchDebounce.toLowerCase());
                })
            );
            setFilteredData(filtered);
        }
    }, [searchDebounce]);

    // Reset to first page when data changes
    useEffect(() => {
        setCurrentPage(1);
    }, [filteredData]);


    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    }

    console.log(filteredData.length,"filteredData.length")
    // Calculate pagination
    const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
    const paginatedData = pagination
        ? filteredData.slice(
            (currentPage - 1) * itemsPerPage,
            Math.min(currentPage * itemsPerPage, filteredData.length)
        )
        : filteredData;


    return (
        <div className="main-container">
            <div className="card table-card-container">
                <div className="card-header table-card-header d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">{cardTitle}</h5>
                    {onCreateClick && (
                        <button
                            className="btn btn-info text-light"
                            onClick={onCreateClick}
                        >
                            <FaPlus className="me-2" />
                            {createButtonText}
                        </button>
                    )}
                </div>
                <div className="card-body table-card-body">
                    {
                        hideSearch ? <>
                            <div className="table-header-container">
                                <div className="search-container">
                                    <div className="input-group">
                                        <input
                                    type="text"
                                    className="search-input form-control"
                                    placeholder={searchPlaceholder}
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    // disabled={loading}
                                />
                                <span className="input-group-text">
                                    <FaSearch />
                                </span>
                                    </div>
                                </div>
                                <div className="action-buttons-container">
                                    {actionButtons && actionButtons}
                                </div>
                            </div>
                        </> : null
                    }


                    <div className="table-responsive-container">
                        <table className="table">
                            <thead>
                                <tr>
                                    {columns.map((column, index) => (
                                        <th key={`th-${index}`} style={column.headerStyle || {}}>
                                            {column.title}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedData.length > 0 ? (
                                    paginatedData.map((item, rowIndex) => (
                                        <tr key={`row-${rowIndex}`}>
                                            {columns.map((column, colIndex) => (
                                                <td key={`col-${colIndex}`}
                                                    style={{
                                                        ...(column.cellStyle || {}),
                                                        width: column.width,
                                                        maxWidth: column.maxWidth,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {column.render
                                                        ? column.render(item)
                                                        : item[column.key]}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={columns.length} className="text-center py-4">
                                            {searchTerm ? 'No matching records found' : 'No data available'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {pagination && totalPages > 1 && (
                    <div className="card-footer table-card-footer">
                        <div className="pagination-container">
                            <ul className="pagination-list">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                    >
                                        &laquo;
                                    </button>
                                </li>

                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                    <li
                                        key={`page-${page}`}
                                        className={`page-item ${currentPage === page ? 'active' : ''}`}
                                    >
                                        <button
                                            className="page-link "
                                            onClick={() => setCurrentPage(page)}
                                            style={{ width: "30px", borderRadius: "10%", textAlign: "center" }}
                                        >
                                            {page}
                                        </button>
                                    </li>
                                ))}

                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                    >
                                        &raquo;
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

TableComponent.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            render: PropTypes.func,
            headerStyle: PropTypes.object,
            cellStyle: PropTypes.object

        })
    ).isRequired,
    data: PropTypes.array,
    loading: PropTypes.bool,
    createButtonText: PropTypes.string,
    onCreateClick: PropTypes.func,
    searchPlaceholder: PropTypes.string,
    onSearch: PropTypes.func,
    pagination: PropTypes.bool,
    itemsPerPage: PropTypes.number,
    actionButtons: PropTypes.node,
    cardTitle: PropTypes.string
};

TableComponent.defaultProps = {
    data: [],
    loading: false
};

export default TableComponent;